'use client';

import { useTranslations } from 'next-intl';
import { ShieldCheck } from 'lucide-react';
import { type ReactElement, useLayoutEffect, useState } from 'react';
import { PropertyCard, Tooltip, TriggerText, UnstyledButton } from '@zillow/constellation-10';
import { LucideIcon } from '@/src/components/LucideIcon';
import { verifiedInline, verifiedPhotoBadgeContent, verifiedPhotoBadgeTrigger } from './verifiedInline.styles';

const TOOLTIP_VIEWPORT_PADDING = 16;
const TRANSLATE_PATTERN = /^translate\(([-.\d]+)px, ([-.\d]+)px\)$/;

function useTooltipViewportShift(positioner: HTMLDivElement | null, arrow: SVGSVGElement | null) {
  useLayoutEffect(() => {
    if (!positioner || !arrow) return;

    let baseX = 0;
    let baseY = 0;
    let currentShift = 0;
    let adjustedTransform = '';

    const updateShift = () => {
      if (positioner.style.transform !== adjustedTransform) {
        const match = positioner.style.transform.match(TRANSLATE_PATTERN);
        if (!match) return;
        baseX = Number(match[1]);
        baseY = Number(match[2]);
        currentShift = 0;
      }

      const viewport = window.visualViewport;
      const viewportLeft = viewport?.offsetLeft ?? 0;
      const viewportWidth = viewport?.width ?? document.documentElement.clientWidth;
      const rect = positioner.getBoundingClientRect();
      const unshiftedLeft = rect.left - currentShift;
      const unshiftedRight = rect.right - currentShift;
      const leftBoundary = viewportLeft + TOOLTIP_VIEWPORT_PADDING;
      const rightBoundary = viewportLeft + viewportWidth - TOOLTIP_VIEWPORT_PADDING;
      currentShift =
        unshiftedLeft < leftBoundary
          ? leftBoundary - unshiftedLeft
          : unshiftedRight > rightBoundary
            ? rightBoundary - unshiftedRight
            : 0;
      adjustedTransform = `translate(${baseX + currentShift}px, ${baseY}px)`;
      const arrowTranslate = `${-currentShift}px 0px`;

      if (positioner.style.transform !== adjustedTransform) positioner.style.transform = adjustedTransform;
      if (arrow.style.translate !== arrowTranslate) arrow.style.translate = arrowTranslate;
    };

    updateShift();

    const resizeObserver = new ResizeObserver(updateShift);
    const mutationObserver = new MutationObserver(updateShift);
    resizeObserver.observe(positioner);
    mutationObserver.observe(positioner, { attributes: true, attributeFilter: ['style'] });
    window.addEventListener('resize', updateShift);
    window.visualViewport?.addEventListener('resize', updateShift);
    window.visualViewport?.addEventListener('scroll', updateShift);

    return () => {
      resizeObserver.disconnect();
      mutationObserver.disconnect();
      window.removeEventListener('resize', updateShift);
      window.visualViewport?.removeEventListener('resize', updateShift);
      window.visualViewport?.removeEventListener('scroll', updateShift);
    };
  }, [arrow, positioner]);
}

type VerifiedInlineProps = {
  /** e.g. a card's `stopCardNavigation`, so tapping the label doesn't trigger navigation. */
  onLabelClick?: (e: React.MouseEvent) => void;
  labelKey?: 'verified' | 'verifiedListing';
  'data-testid'?: string;
};

type VerifiedTooltipProps = {
  children: ReactElement;
};

function VerifiedTooltip({ children }: VerifiedTooltipProps) {
  const t = useTranslations('hdp');
  const [tooltipPositioner, setTooltipPositioner] = useState<HTMLDivElement | null>(null);
  const [tooltipArrow, setTooltipArrow] = useState<SVGSVGElement | null>(null);
  useTooltipViewportShift(tooltipPositioner, tooltipArrow);

  return (
    <Tooltip.Root placement="top" shouldCloseOnViewportLeave>
      <Tooltip.Trigger>{children}</Tooltip.Trigger>
      <Tooltip.Portal>
        <Tooltip.Content
          ref={setTooltipPositioner}
          style={{ maxInlineSize: `min(360px, calc(100dvw - ${TOOLTIP_VIEWPORT_PADDING * 2}px))` }}
        >
          {t('verifiedTooltip')}
          <Tooltip.Arrow ref={setTooltipArrow} />
        </Tooltip.Content>
      </Tooltip.Portal>
    </Tooltip.Root>
  );
}

/**
 * Inline "Verified" indicator (verified icon + tooltip-triggering label) that
 * sits at the end of the HDP's `ListingSummaryModule` meta row.
 */
export function VerifiedInline({ onLabelClick, labelKey = 'verified', 'data-testid': testId }: VerifiedInlineProps) {
  const t = useTranslations('hdp');

  return (
    <span className={verifiedInline} data-testid={testId}>
      {/* eslint-disable-next-line hotpads/no-magic-numbers -- 1em matches the font-size exactly; inline style beats Constellation's Panda atomic size class */}
      <LucideIcon icon={ShieldCheck} size="sm" style={{ width: '1em', height: '1em' }} />
      <VerifiedTooltip>
        <TriggerText css={{ color: 'inherit' }} onClick={onLabelClick}>
          {t(labelKey)}
        </TriggerText>
      </VerifiedTooltip>
    </span>
  );
}

export function VerifiedPhotoBadge({ onLabelClick, 'data-testid': testId }: VerifiedInlineProps) {
  const t = useTranslations('hdp');

  return (
    <VerifiedTooltip>
      <UnstyledButton css={verifiedPhotoBadgeTrigger} onClick={onLabelClick} data-testid={testId}>
        <PropertyCard.Badge tone="neutral">
          <span className={verifiedPhotoBadgeContent}>
            <LucideIcon icon={ShieldCheck} size="sm" />
            {t('verified')}
          </span>
        </PropertyCard.Badge>
      </UnstyledButton>
    </VerifiedTooltip>
  );
}
